import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { ClipboardList, ExternalLink, Send, Copy, CheckCircle2, Clock, Sparkles } from "lucide-react";

/** Admin — list every AI Visibility intake, resend links, view submissions. */
export default function Intakes() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/intakes");
      setRows(data.intakes || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const resendLink = async (id) => {
    try {
      await api.post(`/intakes/${id}/send-magic-link`);
      toast.success("Magic link re-emailed.");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const copyLink = async (id) => {
    try {
      const { data } = await api.get(`/intakes/${id}`);
      const url = data.magic_link_url;
      if (!url) return toast.error("No magic link stored");
      await navigator.clipboard.writeText(url);
      setCopied(id);
      setTimeout(() => setCopied(""), 1500);
      toast.success("Magic link copied");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const totals = {
    all: rows.length,
    pending: rows.filter((r) => r.status === "pending").length,
    in_progress: rows.filter((r) => r.status === "in_progress").length,
    submitted: rows.filter((r) => r.status === "submitted").length,
  };

  return (
    <div className="p-10" data-testid="intakes-page">
      <Toaster position="top-right" richColors />
      <div className="mb-8">
        <div className="overline mb-2">Client onboarding</div>
        <h1 className="font-display text-4xl font-black tracking-tight flex items-center gap-3">
          <ClipboardList className="w-8 h-8 text-[#E24E1B]" /> AI Visibility Intakes
        </h1>
        <p className="text-neutral-600 mt-2 max-w-3xl">
          Every client who bought an AI Visibility package gets a magic-link intake form emailed to them. Once submitted, generate their audit report on the client detail page.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: "Total", value: totals.all },
          { label: "Pending (not opened)", value: totals.pending },
          { label: "In progress", value: totals.in_progress },
          { label: "Submitted", value: totals.submitted },
        ].map((s) => (
          <div key={s.label} className="bg-white border border-neutral-200 p-4">
            <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">{s.label}</div>
            <div className="font-display text-3xl font-bold text-[#0F2340]">{s.value}</div>
          </div>
        ))}
      </div>

      {loading ? <div>Loading…</div> : rows.length === 0 ? (
        <div className="bg-white border border-neutral-200 p-10 text-center text-neutral-500">
          No intakes yet. They&apos;re auto-created when a client activates any AI Visibility add-on. You can also trigger one manually from the client&apos;s detail page.
        </div>
      ) : (
        <div className="bg-white border border-neutral-200 overflow-x-auto">
          <table className="lf-table w-full text-sm" data-testid="intakes-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Status</th>
                <th>Created</th>
                <th>Submitted</th>
                <th>Emails sent</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`intake-row-${r.id}`}>
                  <td>
                    <div className="font-semibold">{r.client_business_name}</div>
                    <div className="text-xs text-neutral-500 font-mono">{r.client_email || "—"}</div>
                  </td>
                  <td>
                    <StatusBadge status={r.status} />
                  </td>
                  <td className="text-xs text-neutral-500 font-mono">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="text-xs text-neutral-500 font-mono">
                    {r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="text-xs text-neutral-500 font-mono">
                    {r.email_send_count || 0}
                  </td>
                  <td className="text-right whitespace-nowrap">
                    <Link to={`/reports/ai-visibility?client_id=${r.client_id}`}
                          data-testid={`intake-view-audit-${r.id}`}
                          className={`text-xs font-semibold px-2 py-1 mr-1 inline-flex items-center gap-1 ${r.status === "submitted" ? "bg-[#E24E1B] text-white hover:bg-[#0F2340]" : "bg-neutral-100 text-neutral-400 pointer-events-none"}`}
                          title={r.status === "submitted" ? "View audit report" : "Submit intake first"}>
                      <Sparkles className="w-3 h-3" /> Audit
                    </Link>
                    <button onClick={() => copyLink(r.id)}
                            data-testid={`intake-copy-${r.id}`}
                            className="text-neutral-600 hover:text-[#0F2340] p-1 mr-1" title="Copy magic link">
                      {copied === r.id ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                    <button onClick={() => resendLink(r.id)}
                            data-testid={`intake-resend-${r.id}`}
                            className="text-blue-600 hover:text-blue-800 p-1 mr-1" title="Resend email">
                      <Send className="w-3.5 h-3.5" />
                    </button>
                    <Link to={`/clients/${r.client_id}`}
                          data-testid={`intake-view-client-${r.id}`}
                          className="text-neutral-600 hover:text-[#0F2340] p-1" title="Open client">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { label: "Pending", color: "bg-amber-50 border-amber-200 text-amber-800", icon: <Clock className="w-3 h-3" /> },
    in_progress: { label: "In progress", color: "bg-blue-50 border-blue-200 text-blue-800", icon: <Clock className="w-3 h-3" /> },
    submitted: { label: "Submitted", color: "bg-emerald-50 border-emerald-200 text-emerald-800", icon: <CheckCircle2 className="w-3 h-3" /> },
  };
  const m = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1 text-xs border px-2 py-0.5 ${m.color}`}>
      {m.icon} {m.label}
    </span>
  );
}
